import { useEffect, useRef, useState } from "react";
import { transcribeMemory } from "./memoryApi";

export const useMemoryRecorder = ({ sessionId, onTranscript, onError }) => {
  const [state, setState] = useState("idle");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const cleanUp = () => {
    window.clearTimeout(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  const stop = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      onError("Voice recording is not available in this browser.");
      return;
    }
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const type = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((item) => MediaRecorder.isTypeSupported(item));
      const recorder = type ? new MediaRecorder(stream, { mimeType: type }) : new MediaRecorder(stream);
      const chunks = [];
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      recorder.onerror = () => { cleanUp(); setState("idle"); onError("That recording did not work. Please try again."); };
      recorder.onstop = async () => {
        const audio = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        cleanUp();
        setState("transcribing");
        try {
          const response = await transcribeMemory(audio, sessionId);
          onTranscript(response.text);
        } catch (error) {
          onError(error.message);
        } finally {
          setState("idle");
        }
      };
      recorder.start(250);
      setState("recording");
      timerRef.current = window.setTimeout(stop, 30000);
    } catch {
      cleanUp();
      setState("idle");
      onError("Allow microphone access to speak your happening.");
    }
  };

  useEffect(() => cleanUp, []);
  return { state, toggle: state === "recording" ? stop : start };
};